"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createParagraphNode, $getNodeByKey, $getRoot, $isParagraphNode } from "lexical";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import { $isTableNode, INSERT_TABLE_COMMAND } from "@lexical/table";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, $isListItemNode, $isListNode } from "@lexical/list";
import {
  IconPilcrow,
  IconH1,
  IconH2,
  IconH3,
  IconH4,
  IconH5,
  IconH6,
  IconListNumbers,
  IconList,
  IconTable,
  IconCopy,
  IconCut,
  IconTrash,
  IconCheck,
  IconArrowsHorizontal,
} from "@tabler/icons-react";
import { Dropdown, Label, Separator } from "@heroui/react";

import { getHeadingLevelLabel } from "../../kernel/constants";
import {
  collectRichTextClipboardForBlockKeys,
  hasRichTextLexicalPayload,
  startRichTextMenuClipboardWrite,
  tryNativeExecCopyForBlockKeys,
} from "../../kernel/helpers/richTextBlockClipboard";
import { $isImageBlockNode } from "../../kernel/nodes/ImageBlockNode";
import { $fitTableColWidthsToHostContentWidth, getHostContentWidthPxForTable } from "./tableFitHostWidth";

export function DragHandleMenu({
  isEditable,
  dragMenuOpen,
  dragMenuPos,
  dragMenuKey,
  selectedKeys,
  onRequestClose,
  containerRef,
}: {
  isEditable: boolean;
  dragMenuOpen: boolean;
  dragMenuPos: { top: number; left: number } | null;
  dragMenuKey: string | null;
  selectedKeys: string[];
  onRequestClose: () => void;
  /** 与 `RichTextEditor` 外层一致，用于「表格宽度适应」内容区宽度 */
  containerRef: React.RefObject<HTMLElement | null>;
}) {
  const [editor] = useLexicalComposerContext();
  const [currentBlockType, setCurrentBlockType] = React.useState<
    "paragraph" | `heading-${1 | 2 | 3 | 4 | 5 | 6}` | "ul" | "ol" | "table" | "image" | "unknown"
  >("unknown");

  React.useEffect(() => {
    if (!dragMenuOpen || !dragMenuKey) return;
    editor.getEditorState().read(() => {
      const node = $getNodeByKey(dragMenuKey);
      if ($isImageBlockNode(node)) {
        setCurrentBlockType("image");
        return;
      }
      if ($isParagraphNode(node)) {
        setCurrentBlockType("paragraph");
        return;
      }
      if ($isListItemNode(node)) {
        const listNode = node.getParent();
        if ($isListNode(listNode)) {
          setCurrentBlockType(listNode.getTag() === "ol" ? "ol" : "ul");
          return;
        }
      }
      if ($isListNode(node)) {
        setCurrentBlockType(node.getTag() === "ol" ? "ol" : "ul");
        return;
      }
      if ($isTableNode(node)) {
        setCurrentBlockType("table");
        return;
      }
      if ($isHeadingNode(node)) {
        const tag = (node as any).getTag?.();
        if (tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4" || tag === "h5" || tag === "h6") {
          setCurrentBlockType(`heading-${Number(tag.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6}`);
          return;
        }
      }
      setCurrentBlockType("unknown");
    });
  }, [dragMenuKey, dragMenuOpen, editor]);

  const convertDragMenuToParagraph = React.useCallback(() => {
    const targetKey = dragMenuKey;
    if (!targetKey) return;
    editor.update(() => {
      const node = $getNodeByKey(targetKey);
      if ($isImageBlockNode(node)) {
        const para = $createParagraphNode();
        node.replace(para);
        (para as any).selectStart?.();
        return;
      }
      if ($isParagraphNode(node)) {
        (node as any).clear?.();
        return;
      }
      if ($isHeadingNode(node)) {
        const children = node.getChildren();
        const para = $createParagraphNode();
        para.append(...children);
        node.replace(para);
        (para as any).selectStart?.();
        return;
      }
      if ($isTableNode(node)) {
        const para = $createParagraphNode();
        node.replace(para);
        (para as any).selectStart?.();
      }
    });
    editor.focus();
    onRequestClose();
  }, [dragMenuKey, editor, onRequestClose]);

  const convertDragMenuToHeading = React.useCallback(
    (level: 1 | 2 | 3 | 4 | 5 | 6) => {
      const targetKey = dragMenuKey;
      if (!targetKey) return;
      editor.update(() => {
        const node = $getNodeByKey(targetKey);
        if ($isImageBlockNode(node)) {
          const heading = $createHeadingNode(`h${level}` as any);
          node.replace(heading);
          (heading as any).selectStart?.();
          return;
        }
        if ($isHeadingNode(node)) {
          const children = node.getChildren();
          const heading = $createHeadingNode(`h${level}` as any);
          heading.append(...children);
          node.replace(heading);
          (heading as any).selectStart?.();
          return;
        }
        if ($isParagraphNode(node)) {
          const children = node.getChildren();
          const heading = $createHeadingNode(`h${level}` as any);
          heading.append(...children);
          node.replace(heading);
          (heading as any).selectStart?.();
        }
      });
      editor.focus();
      onRequestClose();
    },
    [dragMenuKey, editor, onRequestClose],
  );

  const convertDragMenuToList = React.useCallback(
    (kind: "ul" | "ol") => {
      const targetKey = dragMenuKey;
      if (!targetKey) return;

      editor.update(() => {
        const node = $getNodeByKey(targetKey);
        if ($isImageBlockNode(node)) {
          const para = $createParagraphNode();
          node.replace(para);
          (para as any).selectStart?.();
          return;
        }
        if ($isHeadingNode(node)) {
          const children = node.getChildren();
          const para = $createParagraphNode();
          para.append(...children);
          node.replace(para);
          (para as any).selectStart?.();
          return;
        }
        if ($isParagraphNode(node)) {
          (node as any).selectStart?.();
          return;
        }
      });

      editor.focus();
      if (kind === "ul") editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      if (kind === "ol") editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      onRequestClose();
    },
    [dragMenuKey, editor, onRequestClose],
  );

  const convertDragMenuToTable = React.useCallback(() => {
    const targetKey = dragMenuKey;
    if (!targetKey) return;
    editor.update(() => {
      const node = $getNodeByKey(targetKey);
      if (!node) return;
      (node as any).selectStart?.();
    });
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns: "3", rows: "3", includeHeaders: false });
    editor.focus();
    onRequestClose();
  }, [dragMenuKey, editor, onRequestClose]);

  const copyDragMenuBlock = React.useCallback(() => {
    if (!dragMenuKey) return;
    const keys = selectedKeys.length ? selectedKeys : [dragMenuKey];
    if (tryNativeExecCopyForBlockKeys(editor, keys)) {
      onRequestClose();
      return;
    }
    const clip = collectRichTextClipboardForBlockKeys(editor, keys);
    if (
      hasRichTextLexicalPayload(clip) ||
      (clip["text/html"] ?? "").trim().length > 0 ||
      (clip["text/plain"] ?? "").trim().length > 0
    ) {
      startRichTextMenuClipboardWrite(clip);
    } else {
      const text = editor.getEditorState().read(() => {
        return keys
          .map((k) => {
            const node = $getNodeByKey(k);
            return node ? node.getTextContent() : "";
          })
          .filter(Boolean)
          .join("\n");
      });
      if (text) void navigator.clipboard.writeText(text);
    }
    onRequestClose();
  }, [dragMenuKey, editor, onRequestClose, selectedKeys]);

  const deleteByKeys = React.useCallback(
    (keysToDelete: string[]) => {
      onRequestClose();
      editor.update(() => {
        for (const key of keysToDelete) {
          const node = $getNodeByKey(key);
          if ($isImageBlockNode(node) || $isParagraphNode(node) || $isHeadingNode(node) || $isListItemNode(node) || $isTableNode(node)) {
            (node as any).remove?.();
          }
        }
        const root = $getRoot();
        if (root.getChildrenSize() === 0) root.append($createParagraphNode());
      });
      editor.focus();
    },
    [editor, onRequestClose],
  );

  const cutDragMenuBlock = React.useCallback(() => {
    if (!dragMenuKey) return;
    const keysToDelete = selectedKeys.length ? selectedKeys : [dragMenuKey];
    if (!tryNativeExecCopyForBlockKeys(editor, keysToDelete)) {
      const clip = collectRichTextClipboardForBlockKeys(editor, keysToDelete);
      if (
        hasRichTextLexicalPayload(clip) ||
        (clip["text/html"] ?? "").trim().length > 0 ||
        (clip["text/plain"] ?? "").trim().length > 0
      ) {
        startRichTextMenuClipboardWrite(clip);
      } else {
        const text = editor.getEditorState().read(() => {
          return keysToDelete
            .map((k) => {
              const node = $getNodeByKey(k);
              return node ? node.getTextContent() : "";
            })
            .filter(Boolean)
            .join("\n");
        });
        if (text) void navigator.clipboard.writeText(text);
      }
    }
    deleteByKeys(keysToDelete);
  }, [deleteByKeys, dragMenuKey, editor, selectedKeys]);

  const deleteDragMenuBlock = React.useCallback(() => {
    if (!dragMenuKey) return;
    const keysToDelete = selectedKeys.length ? selectedKeys : [dragMenuKey];
    deleteByKeys(keysToDelete);
  }, [deleteByKeys, dragMenuKey, selectedKeys]);

  const fitTableWidthToHost = React.useCallback(() => {
    const key = dragMenuKey;
    const host = containerRef.current;
    if (!key || !host) return;
    const targetW = getHostContentWidthPxForTable(host);
    if (targetW <= 0) return;
    editor.update(() => {
      $fitTableColWidthsToHostContentWidth(key, targetW);
    });
    requestAnimationFrame(() => {
      editor.getEditorState().read(() => {
        const rootEl = editor.getElementByKey(key);
        const tbl =
          rootEl instanceof HTMLTableElement ? rootEl : (rootEl?.querySelector("table") as HTMLTableElement | null);
        if (tbl) {
          tbl.style.width = "";
          tbl.style.maxWidth = "none";
        }
      });
    });
    editor.focus();
    onRequestClose();
  }, [containerRef, dragMenuKey, editor, onRequestClose]);

  const convertOptions = React.useMemo(() => {
    return [
      { key: "paragraph", label: "段落", icon: IconPilcrow, onClick: () => convertDragMenuToParagraph() },
      { key: "heading-1", label: getHeadingLevelLabel(1), icon: IconH1, onClick: () => convertDragMenuToHeading(1) },
      { key: "heading-2", label: getHeadingLevelLabel(2), icon: IconH2, onClick: () => convertDragMenuToHeading(2) },
      { key: "heading-3", label: getHeadingLevelLabel(3), icon: IconH3, onClick: () => convertDragMenuToHeading(3) },
      { key: "heading-4", label: getHeadingLevelLabel(4), icon: IconH4, onClick: () => convertDragMenuToHeading(4) },
      { key: "heading-5", label: getHeadingLevelLabel(5), icon: IconH5, onClick: () => convertDragMenuToHeading(5) },
      { key: "heading-6", label: getHeadingLevelLabel(6), icon: IconH6, onClick: () => convertDragMenuToHeading(6) },
      { key: "ol", label: "有序列表", icon: IconListNumbers, onClick: () => convertDragMenuToList("ol") },
      { key: "ul", label: "无序列表", icon: IconList, onClick: () => convertDragMenuToList("ul") },
      { key: "table", label: "普通表格", icon: IconTable, onClick: () => convertDragMenuToTable() },
    ] as const;
  }, [convertDragMenuToHeading, convertDragMenuToList, convertDragMenuToParagraph, convertDragMenuToTable]);

  const onDragMenuAction = React.useCallback(
    (key: React.Key) => {
      const id = String(key);
      if (id === "__drag_heading" || id === "__drag_table_heading") return;
      if (id === "copy") {
        copyDragMenuBlock();
        return;
      }
      if (id === "cut") {
        cutDragMenuBlock();
        return;
      }
      if (id === "delete") {
        deleteDragMenuBlock();
        return;
      }
      if (id === "table-fit-width") {
        fitTableWidthToHost();
        return;
      }
      const opt = convertOptions.find((o) => o.key === id);
      opt?.onClick();
    },
    [convertOptions, copyDragMenuBlock, cutDragMenuBlock, deleteDragMenuBlock, fitTableWidthToHost],
  );

  if (!isEditable || !dragMenuOpen || !dragMenuPos || !dragMenuKey) return null;

  // 与 SlashMenuPlugin 一致：视口锚点 + 受控 Dropdown + 1px 触发器，Popover 由 React Aria 负责翻转/避障
  return (
    <div className="fixed z-[220]" style={{ top: dragMenuPos.top, left: dragMenuPos.left }}>
      <Dropdown isOpen={dragMenuOpen} onOpenChange={(next) => !next && onRequestClose()}>
        <Dropdown.Trigger className="pointer-events-none absolute left-0 top-0 h-px w-px min-h-px min-w-px overflow-hidden border-0 bg-transparent p-0 opacity-0">
          <span
            data-drag-handle-menu
            data-floating-root="true"
            aria-hidden
            tabIndex={-1}
            className="pointer-events-none block h-px w-px border-0 bg-transparent p-0"
          />
        </Dropdown.Trigger>
        <Dropdown.Popover
          data-drag-handle-menu
          data-floating-root="true"
          className="w-44"
          placement="left"
          offset={8}
        >
          <Dropdown.Menu
            key={currentBlockType === "table" ? "drag-menu-table" : "drag-menu-convert"}
            onAction={onDragMenuAction}
          >
            {currentBlockType === "table" ? (
              <>
                <Dropdown.Item id="__drag_table_heading" isDisabled textValue="表格" className="cursor-default opacity-100">
                  <Label className="text-xs font-medium text-muted-foreground">表格</Label>
                </Dropdown.Item>
                <Dropdown.Item id="table-fit-width" textValue="宽度适应">
                  <IconArrowsHorizontal className="h-4 w-4" />
                  <Label>宽度适应</Label>
                </Dropdown.Item>
              </>
            ) : (
              <>
                <Dropdown.Item id="__drag_heading" isDisabled textValue="转换为" className="cursor-default opacity-100">
                  <Label className="text-xs font-medium text-muted-foreground">转换为</Label>
                </Dropdown.Item>
                {convertOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <Dropdown.Item key={opt.key} id={opt.key} textValue={opt.label}>
                      <Icon className="h-4 w-4" />
                      <Label>{opt.label}</Label>
                      {currentBlockType === opt.key ? (
                        <span className="ml-auto text-primary">
                          <IconCheck className="h-4 w-4" />
                        </span>
                      ) : null}
                    </Dropdown.Item>
                  );
                })}
              </>
            )}

            <Separator className="my-1" />

            <Dropdown.Item id="copy" textValue="复制">
              <IconCopy className="h-4 w-4" />
              <Label>复制</Label>
            </Dropdown.Item>
            <Dropdown.Item id="cut" textValue="剪切">
              <IconCut className="h-4 w-4" />
              <Label>剪切</Label>
            </Dropdown.Item>

            <Separator className="my-1" />

            <Dropdown.Item id="delete" textValue="删除" variant="danger">
              <IconTrash className="h-4 w-4" />
              <Label>删除</Label>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
}
